/**
 * Splitting an export into importable batches.
 *
 * Kept out of the panel so it can be reasoned about (and tested) on its own: the
 * one property that matters is that the batches tile the rows of the current view
 * exactly once, since that is what makes importing them one after another — or
 * concatenating them back together — safe.
 */

/** Row counts that match what import tools usually cap at. */
export const PRESETS = [1000, 5000, 10000, 50000]

/** Past this many files, downloading them one by one is the saner path. */
export const MAX_PARTS = 500

/**
 * @param rows total rows in the view, or null/0 when there is nothing to export
 * @param size requested rows per file; anything unusable falls back to 1
 * @returns {{perFile: number, parts: Array<{part, parts, from, to, rows}>}}
 *   `from`/`to` are 1-based and inclusive over the view's rows
 */
export function planBatches(rows, size) {
  const perFile = Math.max(1, Math.floor(Number(size) || 0))
  if (!Number.isFinite(rows) || rows <= 0) return { perFile, parts: [] }
  const count = Math.ceil(rows / perFile)
  const parts = Array.from({ length: count }, (_, i) => {
    const from = i * perFile + 1
    const to = Math.min((i + 1) * perFile, rows)
    return { part: i + 1, parts: count, from, to, rows: to - from + 1 }
  })
  return { perFile, parts }
}

/** How many leading batches can be set by hand before the equal ones take over. */
export const MAX_RAMP_STEPS = 4

/**
 * A ramp: the first few batches at hand-picked sizes — 100, then 2,000, then
 * 5,000, say, the way a warm-up wants them — and every batch after that at one
 * equal size. The same tiling property holds: every row lands in exactly one
 * batch, in view order, so the files still concatenate back into the whole.
 *
 * @param rows total rows in the view, or null/0 when there is nothing to export
 * @param firstSizes hand-picked sizes for the leading batches, in order; blank
 *   or unusable entries are skipped rather than treated as zero
 * @param size rows per file for every batch after the ramp
 * @returns {{perFile: number, ramp: number[], parts: Array<{part, parts, from, to, rows}>}}
 *   `ramp` is the leading sizes actually in use, after cleaning
 */
export function planRamp(rows, firstSizes, size) {
  const perFile = Math.max(1, Math.floor(Number(size) || 0))
  const ramp = (firstSizes ?? [])
    .slice(0, MAX_RAMP_STEPS)
    .map((s) => Math.floor(Number(s)))
    .filter((s) => Number.isFinite(s) && s >= 1)
  if (!Number.isFinite(rows) || rows <= 0) return { perFile, ramp, parts: [] }

  const parts = []
  let from = 1
  for (const step of ramp) {
    if (from > rows) break
    const to = Math.min(from + step - 1, rows)
    parts.push({ from, to, rows: to - from + 1 })
    from = to + 1
  }
  while (from <= rows) {
    const to = Math.min(from + perFile - 1, rows)
    parts.push({ from, to, rows: to - from + 1 })
    from = to + 1
  }
  const count = parts.length
  return { perFile, ramp, parts: parts.map((p, i) => ({ part: i + 1, parts: count, ...p })) }
}
